import React, { useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SearchIcon, BookmarkIcon, FilterIcon, ListIcon, GridIcon, PlayIcon, BookIcon, GraduationCapIcon, BriefcaseIcon, GlobeIcon, HomeIcon, ShoppingBagIcon, StarIcon } from 'lucide-react';
import { WordCard } from '../components/WordCard';
import { SearchWords } from '../components/SearchWords';
import { FlashcardPractice } from '../components/FlashcardPractice';
import { WordMatchingGame } from '../components/WordMatchingGame';
import { Sitemap } from '../components/Sitemap';
const VOCABULARY_CATEGORIES = [{
  id: 'essential',
  icon: BookIcon,
  title: {
    en: 'Essential Words',
    ar: 'الكلمات الأساسية'
  },
  description: {
    en: 'Most common words used in daily life',
    ar: 'أكثر الكلمات شيوعاً في الحياة اليومية'
  },
  words: [{
    en: 'Hello',
    ar: 'مرحبا',
    pronunciation: 'hə-ˈlō',
    examples: [{
      en: 'Hello, how are you?',
      ar: 'مرحبا، كيف حالك؟'
    }, {
      en: 'Hello everyone!',
      ar: 'مرحبا بالجميع!'
    }]
  }, {
    en: 'Thank you',
    ar: 'شكراً',
    pronunciation: 'thank-you',
    examples: [{
      en: 'Thank you very much.',
      ar: 'شكراً جزيلاً.'
    }, {
      en: 'Thank you for your help.',
      ar: 'شكراً على مساعدتك.'
    }]
  }, {
    en: 'Please',
    ar: 'من فضلك',
    pronunciation: 'plēz'
  }]
}, {
  id: 'business',
  icon: BriefcaseIcon,
  title: {
    en: 'Business English',
    ar: 'الإنجليزية للأعمال'
  },
  description: {
    en: 'Professional vocabulary for work',
    ar: 'مفردات مهنية للعمل'
  },
  words: [{
    en: 'Meeting',
    ar: 'اجتماع',
    pronunciation: 'mē-tiŋ',
    examples: [{
      en: 'The meeting starts at 9 AM',
      ar: 'يبدأ الاجتماع الساعة 9 صباحاً'
    }]
  }]
}, {
  id: 'travel',
  icon: GlobeIcon,
  title: {
    en: 'Travel & Tourism',
    ar: 'السفر والسياحة'
  },
  description: {
    en: 'Essential words for traveling',
    ar: 'كلمات أساسية للسفر'
  },
  words: [{
    en: 'Airport',
    ar: 'مطار',
    pronunciation: 'er-ˌpȯrt',
    examples: [{
      en: 'The airport is very busy today',
      ar: 'المطار مزدحم جداً اليوم'
    }]
  }]
}, {
  id: 'academic',
  icon: GraduationCapIcon,
  title: {
    en: 'Academic Words',
    ar: 'الكلمات الأكاديمية'
  },
  description: {
    en: 'Words commonly used in education',
    ar: 'الكلمات المستخدمة في التعليم'
  },
  words: [{
    en: 'Research',
    ar: 'بحث',
    pronunciation: 'ri-ˈsərch',
    examples: [{
      en: 'She is doing research on climate change',
      ar: 'هي تجري بحثاً عن تغير المناخ'
    }]
  }]
}, {
  id: 'home',
  icon: HomeIcon,
  title: {
    en: 'Home & Family',
    ar: 'المنزل والعائلة'
  },
  description: {
    en: 'Words related to home and family life',
    ar: 'كلمات متعلقة بالمنزل والحياة العائلية'
  },
  words: [{
    en: 'Kitchen',
    ar: 'مطبخ',
    pronunciation: 'ki-chən',
    examples: [{
      en: 'The kitchen is the heart of the home',
      ar: 'المطبخ هو قلب المنزل'
    }]
  }]
}, {
  id: 'shopping',
  icon: ShoppingBagIcon,
  title: {
    en: 'Shopping & Commerce',
    ar: 'التسوق والتجارة'
  },
  description: {
    en: 'Shopping and commerce vocabulary',
    ar: 'مفردات التسوق والتجارة'
  },
  words: [{
    en: 'Price',
    ar: 'سعر',
    pronunciation: 'prīs',
    examples: [{
      en: 'What is the price of this item?',
      ar: 'ما هو سعر هذا المنتج؟'
    }]
  }]
}];
interface FiltersState {
  view: 'grid' | 'list';
  bookmarked: boolean;
  category: string | null;
}
export function VocabularyLists() {
  const {
    isArabic
  } = useLanguage();
  const [search, setSearch] = useState('');
  const [bookmarkedWords, setBookmarkedWords] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<FiltersState>({
    view: 'grid',
    bookmarked: false,
    category: null
  });
  const [practiceMode, setPracticeMode] = useState('flashcards');
  const filteredCategories = useMemo(() => {
    return VOCABULARY_CATEGORIES.map(category => ({
      ...category,
      words: category.words.filter(word => {
        const matchesSearch = search.trim() === '' || word.en.toLowerCase().includes(search.toLowerCase()) || word.ar.includes(search);
        const matchesBookmarked = !filters.bookmarked || bookmarkedWords.has(word.en);
        return matchesSearch && matchesBookmarked;
      })
    })).filter(category => category.words.length > 0 && (!filters.category || category.id === filters.category));
  }, [search, filters.bookmarked, filters.category, bookmarkedWords]);
  const totalResults = filteredCategories.reduce((sum, category) => sum + category.words.length, 0);
  const toggleBookmark = (word: string) => {
    setBookmarkedWords(prev => {
      const next = new Set(prev);
      if (next.has(word)) {
        next.delete(word);
      } else {
        next.add(word);
      }
      return next;
    });
  };
  const flashcards = useMemo(() => {
    return VOCABULARY_CATEGORIES.flatMap(category => category.words.map((word, index) => ({
      id: index,
      en: word.en,
      ar: word.ar,
      pronunciation: word.pronunciation || '',
      difficulty: 'medium' as const
    })));
  }, []);
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-4702">
      <Sitemap currentPage={{
      en: 'Vocabulary Lists',
      ar: 'قوائم المفردات'
    }} breadcrumbs={[{
      label: {
        en: 'Resources',
        ar: 'المصادر'
      },
      href: '/resources'
    }]} data-id="element-4703" />
      <div className="max-w-7xl mx-auto" data-id="element-4704">
        <div className="text-center mb-12" data-id="element-4705">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-4706">
            {isArabic ? 'قوائم المفردات' : 'Vocabulary Lists'}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-4707">
            {isArabic ? 'تعلم المفردات الإنجليزية الأساسية مع النطق والأمثلة' : 'Learn essential English vocabulary with pronunciation and examples'}
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-id="element-4708">
          {VOCABULARY_CATEGORIES.map(category => {
          const Icon = category.icon;
          return <button key={category.id} onClick={() => setFilters(f => ({
            ...f,
            category: f.category === category.id ? null : category.id
          }))} className={`
                  p-4 rounded-xl text-center transition-all
                  ${filters.category === category.id ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 ring-2 ring-blue-600 dark:ring-blue-400' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}
                `} data-id="element-4709">
                <Icon className="w-6 h-6 mx-auto mb-2" data-id="element-4710" />
                <div className="font-medium" data-id="element-4711">
                  {isArabic ? category.title.ar : category.title.en}
                </div>
              </button>;
        })}
        </div>
        <div className="flex flex-col md:flex-row gap-4 mb-8" data-id="element-4712">
          <SearchWords value={search} onChange={setSearch} onClear={() => setSearch('')} resultsCount={totalResults} className="flex-1" data-id="element-4713" />
          <div className="flex gap-2" data-id="element-4714">
            <button onClick={() => setFilters(f => ({
            ...f,
            bookmarked: !f.bookmarked
          }))} className={`
                px-4 py-2 rounded-lg flex items-center gap-2
                ${filters.bookmarked ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'}
              `} data-id="element-4715">
              <BookmarkIcon className="w-5 h-5" data-id="element-4716" />
              <span data-id="element-4717">{isArabic ? 'المحفوظة' : 'Bookmarked'}</span>
            </button>
            <button onClick={() => setFilters(f => ({
            ...f,
            view: f.view === 'grid' ? 'list' : 'grid'
          }))} className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-600 dark:text-gray-300" data-id="element-4718">
              {filters.view === 'grid' ? <ListIcon className="w-5 h-5" data-id="element-4719" /> : <GridIcon className="w-5 h-5" data-id="element-4720" />}
            </button>
          </div>
        </div>
        <div className="space-y-12" data-id="element-4721">
          <div className="mb-12 bg-gradient-to-br from-blue-50 to-white dark:from-gray-800/50 dark:to-gray-900/50 rounded-2xl p-8" data-id="element-4722">
            <div className="max-w-2xl mx-auto text-center mb-8" data-id="element-4723">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-4724">
                {isArabic ? 'تدرب على المفردات' : 'Practice Vocabulary'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6" data-id="element-4725">
                {isArabic ? 'تحسين مفرداتك من خلال البطاقات التفاعلية ومطابقة الكلمات' : 'Improve your vocabulary with flashcards and word matching'}
              </p>
              <div className="flex flex-wrap justify-center gap-6 mb-8" data-id="element-4726">
                <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-sm" data-id="element-4727">
                  <BookIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-4728" />
                  <span className="text-gray-600 dark:text-gray-400" data-id="element-4729">
                    {flashcards.length} {isArabic ? 'بطاقة' : 'cards'}
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-sm" data-id="element-4730">
                  <StarIcon className="w-5 h-5 text-yellow-500" data-id="element-4731" />
                  <span className="text-gray-600 dark:text-gray-400" data-id="element-4732">
                    {isArabic ? 'تعلم تفاعلي' : 'Interactive learning'}
                  </span>
                </div>
              </div>
              <div className="max-w-2xl mx-auto mb-8" data-id="element-4733">
                <div className="flex justify-center space-x-4" data-id="element-4734">
                  <button onClick={() => setPracticeMode('flashcards')} className={`px-4 py-2 rounded-lg transition-colors ${practiceMode === 'flashcards' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`} data-id="element-4735">
                    {isArabic ? 'البطاقات التعليمية' : 'Flashcards'}
                  </button>
                  <button onClick={() => setPracticeMode('matching')} className={`px-4 py-2 rounded-lg transition-colors ${practiceMode === 'matching' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`} data-id="element-4736">
                    {isArabic ? 'مطابقة الكلمات' : 'Word Matching'}
                  </button>
                </div>
              </div>
              {practiceMode === 'flashcards' ? <FlashcardPractice cards={flashcards} data-id="element-4737" /> : <WordMatchingGame data-id="element-4738" />}
            </div>
          </div>
          {filteredCategories.map(category => <div key={category.id} data-id="element-4739">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-4740">
                {isArabic ? category.title.ar : category.title.en}
              </h2>
              <div className={`
                  grid gap-6
                  ${filters.view === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}
                `} data-id="element-4741">
                {category.words.map((word, wordIndex) => <WordCard key={wordIndex} word={word} isBookmarked={bookmarkedWords.has(word.en)} onBookmark={() => toggleBookmark(word.en)} data-id="element-4742" />)}
              </div>
            </div>)}
        </div>
        {filteredCategories.length === 0 && <div className="text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-4743">
            {isArabic ? 'لا توجد كلمات تطابق بحثك' : 'No words match your search'}
          </div>}
      </div>
    </div>;
}